import { readJumpstartQueueSearchState } from "@/api/queueSearchState";
import {
  isQueueHistory,
  prepareQueueSearch,
  queueResultCount,
  queueStatusLine,
  searchQueueItems,
} from "@/lib/queueSearch";
import { QueueHistoryLinks } from "./QueueHistoryLinks";
import { useCallback, useEffect, useState } from "react";
import {
  migrationQueueApi,
  type QueueItem,
  type QueueState,
} from "@/api/migrationQueue";
import { queueErrorMessage } from "@/lib/queueBreakGlass";
import { sharedPoll, stopPoll } from "@/command/poll";

export function DbQueueSection(
  { searchQuery, onSearchChange }: {
    searchQuery?: string;
    onSearchChange?: (query: string) => void;
  } = {},
) {
  const [localSearch, setLocalSearch] = useState("");
  const query = searchQuery ?? localSearch;
  const setQuery = onSearchChange ?? setLocalSearch;
  const [state, setState] = useState<QueueState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [breakGlassReason, setBreakGlassReason] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [sql, setSql] = useState<
    { id: string; version: string; text: string } | null
  >(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const next = await readJumpstartQueueSearchState();
      setState({
        ...next,
        items: next.items,
      });
      setError(null);
      return true;
    } catch (err) {
      setState(null);
      setError(queueErrorMessage(err));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    const timer = sharedPoll(load, 30_000, true);
    return () => stopPoll(timer);
  }, [load]);

  async function viewSql(item: QueueItem) {
    setBusy(item.id);
    setError(null);
    setSql(null);
    try {
      const response = await migrationQueueApi.state(item.id, true);
      const found = response.items.find((row) => row.id === item.id);
      if (typeof found?.sql !== "string") throw new Error("SQL unavailable");
      const actual = await prepareQueueSearch({
        ...found,
        sql_sha256: undefined,
      });
      if (item.sql_sha256 && actual.sql_sha256 !== item.sql_sha256) {
        throw Error("SQL changed");
      }
      setSql({ id: item.id, version: item.version, text: found.sql });
    } catch (err) {
      setError(queueErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }
  async function act(
    item: QueueItem,
    action: "approve" | "hold" | "release" | "cancel",
  ) {
    const labels = {
      approve: "אישור חירום",
      hold: "השהיה",
      release: "שחרור השהיה",
      cancel: "ביטול",
    };
    if (
      !window.confirm(
        `${labels[action]}: ${item.version} ${item.name}?${
          action === "approve"
            ? "\nאישור מאפשר לרץ לבצע את שינוי בסיס הנתונים."
            : ""
        }`,
      )
    ) return;
    const reason = window.prompt(`סיבה ל${labels[action]}`);
    if (!reason?.trim()) return;
    setBusy(item.id);
    setError(null);
    try {
      await migrationQueueApi.action(item.id, action, reason.trim());
      await load();
    } catch (err) {
      setError(queueErrorMessage(err));
      await load();
      setError(queueErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }
  async function changeBreakGlass(action: "open" | "close") {
    const reason = breakGlassReason.trim();
    if (!reason || !state) return;
    const label = action === "open"
      ? "פתיחת break-glass ל־30 דקות"
      : "סגירת break-glass";
    if (
      !window.confirm(
        `${label}?\nהפעולה אינה מאשרת מיגרציות ואינה משנה את חלון הריצה של התור.\nסיבה: ${reason}`,
      )
    ) return;
    setBusy("break-glass");
    setError(null);
    setNotice(null);
    try {
      await migrationQueueApi.breakGlass(action, reason);
      setBreakGlassReason("");
      setNotice(
        action === "open"
          ? "בקשת הפתיחה אושרה. מצב החלון המעודכן מוצג לאחר רענון התור."
          : "בקשת הסגירה אושרה. מצב החלון המעודכן מוצג לאחר רענון התור.",
      );
      await load();
    } catch (err) {
      setError(queueErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }
  const breakGlassActive = Boolean(
    state?.break_glass_active_until &&
      Date.parse(state.break_glass_active_until) > Date.now(),
  );
  const items = searchQueueItems(state?.items ?? [], query);
  return (
    <section
      aria-label="DB queue"
      className="mb-5 rounded-xl border border-navy-border bg-navy-card p-4 [&_button]:min-h-11 [&_button]:min-w-11"
      dir="rtl"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold">
          תור שינויי בסיס נתונים — DB queue {state && `(${items.length})`}
        </h2>
        <button
          disabled={loading || busy !== null}
          onClick={() => void load()}
          className="rounded-lg px-3 py-2 text-sm text-indigo-300 disabled:opacity-40"
        >
          רענון
        </button>
      </div>
      <p className="mt-2 text-xs text-ink-secondary">
        תור JumpStart, ללא תלות בסנכרון PRs. אישור ידני זמין לפריטי חירום בלבד;
        פריטים רגילים מטופלים ברץ הלילי.
      </p>
      {state && (
        <p className="mt-2 text-xs text-ink-secondary">
          {state.paused ? "הרץ מושהה" : "הרץ פעיל"} ·{" "}
          {state.window_open ? "חלון לילה פתוח" : "מחוץ לחלון הלילה"}
        </p>
      )}
      <div
        className="mt-3 rounded-lg border border-amber-500/40 p-3"
        aria-label="חלון break-glass"
      >
        <h3 className="font-medium">חלון break-glass</h3>
        <p className="mt-1 text-xs text-ink-secondary">
          חריג זמני למחסום DDL, למנהל ראשי בלבד. אינו מאשר פריטים ואינו פותח את
          חלון הריצה של התור. אין להריץ DDL ידני במסגרת שחרור WhatsApp.
        </p>
        {state && (
          <p role="status" className="mt-2 text-sm">
            מחסום DDL: {state.guard_on ? "פעיל" : "כבוי"} · {breakGlassActive
              ? `break-glass פעיל עד ${
                new Intl.DateTimeFormat("he-IL", {
                  timeZone: "Asia/Jerusalem",
                  dateStyle: "short",
                  timeStyle: "medium",
                }).format(new Date(state.break_glass_active_until!))
              }`
              : "break-glass סגור"}
          </p>
        )}
        <label className="mt-2 block text-xs text-ink-secondary">
          סיבה לפתיחה או לסגירה<input
            aria-label="סיבה ל-break-glass"
            value={breakGlassReason}
            onChange={(event) => setBreakGlassReason(event.target.value)}
            className="mt-1 min-h-11 w-full rounded-lg border border-gray-600 bg-gray-900 px-3 text-base text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </label>
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            disabled={!state || loading || busy !== null ||
              !breakGlassReason.trim() || breakGlassActive}
            onClick={() => void changeBreakGlass("open")}
            className="min-h-11 rounded bg-amber-500/20 px-3 py-2 disabled:opacity-40"
          >
            פתיחת break-glass ל־30 דקות
          </button>
          <button
            disabled={!state || loading || busy !== null ||
              !breakGlassReason.trim() || !breakGlassActive}
            onClick={() => void changeBreakGlass("close")}
            className="min-h-11 rounded bg-gray-500/20 px-3 py-2 disabled:opacity-40"
          >
            סגירת break-glass
          </button>
        </div>
        {notice && (
          <p role="status" className="mt-2 text-sm text-emerald-300">
            {notice}
          </p>
        )}
      </div>
      <label className="mt-3 block text-xs text-ink-secondary">
        חיפוש שינויי DB<input
          type="search"
          aria-label="חיפוש שינויי DB"
          dir={query ? "auto" : "rtl"}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="גרסה, שם מיגרציה, מאגר או שגיאה…"
          className="mt-1 min-h-11 w-full rounded-lg border border-gray-600 bg-gray-900 px-3 text-base text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-400"
        />
      </label>
      {state && (
        <p role="status" className="mt-2 text-xs text-ink-secondary">
          {queueResultCount(items)}
          {query.trim() && (
            <button
              onClick={() => setQuery("")}
              className="ms-2 min-h-11 px-2 text-indigo-200 underline"
            >
              נקה חיפוש DB
            </button>
          )}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-3 text-sm text-red-300">
          טעינת התור או הפעולה נכשלה: {error}
        </p>
      )}
      {!state && loading && (
        <p className="mt-3 text-sm text-ink-secondary">טוען תור…</p>
      )}
      {state && !items.length && (
        <p className="mt-3 text-sm text-ink-secondary">
          אין שינויי DB שמתאימים לחיפוש בכל המצבים.
        </p>
      )}
      <div className="mt-3 space-y-3">
        {items.map((item) => {
          const actionable = item.status === "pending" ||
            item.status === "approved";
          return (
            <details
              key={item.id}
              onToggle={(event) => {
                if (event.currentTarget.open) void viewSql(item);
              }}
              className="rounded-lg border border-navy-border p-3 text-sm"
            >
              <summary className="min-h-11 cursor-pointer">
                <span className="flex flex-wrap gap-2">
                  <code dir="ltr">{item.version}</code>
                  <span>{item.name}</span>
                  <span>{item.repo}</span>
                  <strong>{queueStatusLine(item)}</strong>
                  {item.held && <span className="text-amber-300">מושהה</span>}
                  {item.emergency && (
                    <span className="text-amber-300">חירום</span>
                  )}
                </span>
              </summary>
              <QueueHistoryLinks item={item} />
              {item.sql_sha256 && (
                <code className="block break-all" dir="ltr">
                  {item.sql_sha256}
                </code>
              )}
              {!isQueueHistory(item) && !item.pr_url && (
                <p className="mt-2 text-xs text-amber-200">
                  אישור SQL עצמאי דרך חבילת השחרור חסום: חיבור פרוטוקול
                  JumpStart עדיין אינו זמין.
                </p>
              )}
              {item.last_error && (
                <p className="mt-2 break-words text-red-300">
                  {item.last_error}
                </p>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  disabled={busy !== null}
                  onClick={() => void viewSql(item)}
                  className="rounded bg-indigo-500/20 px-3 py-2 disabled:opacity-40"
                >
                  הצגת SQL
                </button>
                {!isQueueHistory(item) && (
                  <>
                    <button
                      title="אישור ידני לפריט חירום ממתין בלבד"
                      disabled={busy !== null || !item.emergency ||
                        item.status !== "pending"}
                      onClick={() => void act(item, "approve")}
                      className="rounded bg-emerald-500/20 px-3 py-2 disabled:opacity-40"
                    >
                      אישור
                    </button>
                    <button
                      disabled={busy !== null || !actionable}
                      onClick={() =>
                        void act(item, item.held ? "release" : "hold")}
                      className="rounded bg-amber-500/20 px-3 py-2 disabled:opacity-40"
                    >
                      {item.held ? "שחרור השהיה" : "השהיה"}
                    </button>
                    <button
                      disabled={busy !== null || !actionable}
                      onClick={() => void act(item, "cancel")}
                      className="rounded bg-red-500/20 px-3 py-2 disabled:opacity-40"
                    >
                      ביטול
                    </button>
                  </>
                )}
              </div>
              {sql?.id === item.id && (
                <pre
                  dir="ltr"
                  className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap break-words text-xs"
                >{sql.text}</pre>
              )}
            </details>
          );
        })}
      </div>
    </section>
  );
}
